// PRD 06 §3.3 — compute per-week question load for the calendar tab.
// Mirrors the prototype's `renderCalendar()` (module-config.html L942–L999).
//
// Rules:
//   - `daily` modules are EXCLUDED from the calendar (per prototype footer).
//   - `weekly` modules contribute on their weekday(s) every week.
//   - `biweekly` modules contribute on odd weeks (1, 3, 5, …).
//   - `monthly` modules contribute on the weeks listed in MONTHLY_WEEKS.
//   - `quarterly` modules contribute on the weeks listed in QUARTER_WEEKS
//     (1, 13, 25 — every 12 weeks / 3×4-week months) and are always drawn in the
//     column where the week starts, Monday. A quarterly item has no weekday of its
//     own, so the preview never reads one, and every quarterly item lands in the
//     same column (FEATURE_025 (PRO Distribution Management)).
//     NOTE: this is the admin workload PREVIEW only — the real per-patient
//     schedule (quarterly = every 90 days from discharge) is computed separately
//     on the server and must NOT follow this display rule.

import type { Module, ProItemRef, Weekday } from '../types/pro-deployment.types';

export const MONTHLY_WEEKS: readonly number[] = [1, 5, 9, 13, 17, 21, 25];
export const QUARTER_WEEKS: readonly number[] = [1, 13, 25];

export const WEEKDAY_ORDER: readonly Weekday[] = [
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
  'SAT',
  'SUN',
];

const QUARTERLY_WEEKDAY: Weekday = 'MON';

export interface WeekLoadRow {
  week: number;
  isMonthly: boolean;
  days: Record<Weekday, number>;
  total: number;
}

function moduleQuestionTotal(
  module: Module,
  itemDict: ReadonlyMap<string, ProItemRef>,
): number {
  let total = 0;
  for (const pid of module.itemIds) {
    total += itemDict.get(pid)?.questionCount ?? 0;
  }
  return total;
}

function isModuleActiveOnWeek(module: Module, week: number): boolean {
  switch (module.frequency) {
    case 'daily':
      return false;
    case 'weekly':
      return true;
    case 'biweekly':
      return week % 2 === 1;
    case 'monthly':
      return MONTHLY_WEEKS.includes(week);
    case 'quarterly':
      return QUARTER_WEEKS.includes(week);
  }
}

function moduleWeekdays(module: Module): Weekday[] {
  if (module.frequency === 'quarterly') {
    return [QUARTERLY_WEEKDAY];
  }
  return module.weekdays;
}

export function computeCalendar(
  modules: readonly Module[],
  items: readonly ProItemRef[],
  monitoringWeeks: number,
): WeekLoadRow[] {
  const itemDict = new Map(items.map((i) => [i.pid, i]));
  const totals = new Map<string, number>();
  for (const m of modules) {
    totals.set(m.mid, moduleQuestionTotal(m, itemDict));
  }

  const rows: WeekLoadRow[] = [];
  for (let w = 1; w <= monitoringWeeks; w++) {
    const days: Record<Weekday, number> = {
      MON: 0,
      TUE: 0,
      WED: 0,
      THU: 0,
      FRI: 0,
      SAT: 0,
      SUN: 0,
    };
    for (const m of modules) {
      if (!isModuleActiveOnWeek(m, w)) continue;
      const q = totals.get(m.mid) ?? 0;
      if (q === 0) continue;
      for (const day of moduleWeekdays(m)) {
        days[day] += q;
      }
    }
    const total = WEEKDAY_ORDER.reduce((acc, d) => acc + days[d], 0);
    rows.push({
      week: w,
      isMonthly: MONTHLY_WEEKS.includes(w),
      days,
      total,
    });
  }
  return rows;
}
